import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Service, ServiceInstance, ServiceStatusEnum } from '@anecdotes/api-contracts';
import { DropdownControl } from '@anecdotes/design-system/dropdown-menu';
import { ChangeDetectorRef, Component, Injector, DestroyRef } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  BehaviorSubject,
  Observable,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { PluginFacadeService } from '@anecdotes/data-store';
import { TranslateService } from '@ngx-translate/core';

export const PLUGIN_ACCOUNT_CONTROL_NAME = 'plugin-account-dropdown-control';

@Component({
  template: '',
  standalone: false,
})
export class PluginAccountDropdownFormExtensionComponent {
  protected readonly destroyRef: DestroyRef;
  private readonly serviceInstanceChosenId = new BehaviorSubject<string>(undefined);

  private accountControl: DropdownControl;
  private _serviceInstanceId: string;
  private _plugin: Service;
  private defaultInstanceId: string;

  protected readonly translate: TranslateService;
  protected readonly cd: ChangeDetectorRef;
  protected readonly pluginFacadeService: PluginFacadeService;


  readonly isPluginLoading$ = new BehaviorSubject<boolean>(false);

  get plugin(): Service {
    return this._plugin;
  }

  get pluginId(): string {
    return this._plugin.service_id;
  }

  get selectedServiceInstanceId(): string {
    return this._serviceInstanceId;
  }

  set selectedServiceInstanceId(value: string) {
    this._serviceInstanceId = value;
  }

  constructor(protected injector: Injector) {
    this.pluginFacadeService = injector.get(PluginFacadeService);
    this.translate = injector.get(TranslateService);
    this.cd = injector.get(ChangeDetectorRef);
    this.destroyRef = injector.get(DestroyRef);
  }

  onInit(
    pluginId: string,
    isServiceMultiAccount: boolean,
    serviceInstanceIdChange?: (selectedServiceInstanceId: string) => void | Promise<void>,
    startListeningWhen?: Observable<boolean>,
    defaultInstanceId?: string,
  ): DropdownControl | null {
    this.defaultInstanceId = defaultInstanceId;
    this.initializePlugin(pluginId);

    this.listenServiceInstanceSelection(serviceInstanceIdChange, startListeningWhen);

    return this.getServiceInstanceControl(isServiceMultiAccount);
  }

  private async initializePlugin(pluginId: string): Promise<void> {
    this.isPluginLoading$.next(true);

    this._plugin = await firstValueFrom(this.pluginFacadeService.loadSpecificPlugin(pluginId));

    if (this.plugin.service_multi_account) {
      this.accountControl.inputs.data = this.plugin.service_instances_list
        .filter(({ service_status }) => service_status === ServiceStatusEnum.INSTALLED);

      this.accountControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((serviceInstanceId: ServiceInstance) => {
          this.serviceInstanceChosenId.next(serviceInstanceId?.service_instance_id);
        });

      if (this.accountControl.inputs.data.length === 1) {
        this.selectFirstServiceInstance();
      } else {
        this.setDefaultServiceInstance();
      }
    } else {
      this.serviceInstanceChosenId.next(
        this.plugin.service_instances_list
          .find((instance) => instance.service_status === ServiceStatusEnum.INSTALLED)
          ?.service_instance_id
      );
    }

    this.isPluginLoading$.next(false);
  }

  private getServiceInstanceControl(isServiceMultiAccount: boolean): DropdownControl | null {
    if (!isServiceMultiAccount) {
      return null;
    }

    this.accountControl = new DropdownControl({
      initialInputs: {
        data: [],
        searchEnabled: true,
        required: true,
        displayValueSelector: (instance: ServiceInstance) => instance.service_instance_display_name,
        titleTranslationKey: this.buildDynamicFormTranslationKey('accountDropdownLabel'),
        searchFieldPlaceholder: this.buildDynamicFormTranslationKey('accountDropdownSearchPlaceholder'),
        placeholderTranslationKey: this.buildDynamicFormTranslationKey('accountDropdownPlaceholder'),
      },
      validators: [Validators.required],
    });

    return this.accountControl;
  }

  private selectFirstServiceInstance(): void {
    this.accountControl.setValue(this.accountControl.inputs.data[0]);
    this.accountControl.inputs.controlTooltip = this.translate.instant(this.buildDynamicFormTranslationKey('disabledAccountTooltip'));
    this.accountControl.inputs.controlTooltipPlacement = 'top';
    this.accountControl.disable();
  }

  private setDefaultServiceInstance(): void {
    if (this.defaultInstanceId) {
      const defaultInstance = this.accountControl.inputs.data.find((instance) => instance.service_instance_id === this.defaultInstanceId);
      if (defaultInstance) {
        this.accountControl.setValue(defaultInstance);
      }
    }
  }

  private listenServiceInstanceSelection(
    successCallback?: (selectedServiceInstanceId: string) => void | Promise<void>,
    startListeningWhen?: Observable<boolean>
  ): void {
    const changesObs$ = this.serviceInstanceChosenId.pipe(
      filter((selected) => !!selected),
      distinctUntilChanged(),
      tap((selectedId) => (this._serviceInstanceId = selectedId)),
    );
    let toSubscribe$ = changesObs$;

    if (startListeningWhen) {
      toSubscribe$ = startListeningWhen.pipe(
        filter(Boolean),
        take(1),
        switchMap(() => changesObs$),
      );
    }

    toSubscribe$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selectedInstanceId) => {
      if (successCallback) {
        successCallback(selectedInstanceId);
      }
    });
  }

  private buildDynamicFormTranslationKey(key: string): string {
    return `pluginAccountDropdown.${key}`;
  }
}
